import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import ArticleCard from '../components/magazine/ArticleCard';
import { listArticles, listCategories } from '../lib/magazineApi';
import MagazineExtras from '../components/magazine/MagazineExtras';

/*
  MagazinePage — Magazine homepage.

  Editorial layout: 1 hero feature, 2-3 picks, latest grid, categories rail.
  Light background to differentiate the long-form reading surface from the
  rest of the dark site, while keeping the Kitxens brand language intact.
*/

export default function MagazinePage() {
  const [params, setParams] = useSearchParams();
  const queryFromUrl = params.get('q') || '';
  const [search, setSearch] = useState(queryFromUrl);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSearch(queryFromUrl);
    setLoading(true);
    Promise.all([
      listArticles({ q: queryFromUrl || undefined, page_size: 24 }),
      listCategories(),
    ])
      .then(([list, cats]) => {
        setArticles(list.items || []);
        setCategories(cats || []);
      })
      .catch(() => {
        setArticles([]);
        setCategories([]);
      })
      .finally(() => setLoading(false));
  }, [queryFromUrl]);

  const onSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (search.trim()) next.set('q', search.trim());
    else next.delete('q');
    setParams(next);
  };

  const isSearching = !!queryFromUrl;
  const [myPins, setMyPins] = useState([]);
  const [popular, setPopular] = useState([]);
  const [pinMeta, setPinMeta] = useState({ folders: {}, streak: 0, badges: [], folder_views: {} });
  const [suggestions, setSuggestions] = useState([]);
  const [activeFolder, setActiveFolder] = useState('');
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins`, { credentials: 'include' })
      .then((r) => r.json()).then((j) => { setMyPins(j.pins || []); setPinMeta({ folders: j.folders || {}, streak: j.streak || 0, badges: j.badges || [], folder_views: j.folder_views || {} }); }).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins/popular`)
      .then((r) => r.json()).then((j) => setPopular(j.popular || [])).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins/suggestions`, { credentials: 'include' })
      .then((r) => r.json()).then((j) => setSuggestions(j.suggestions || [])).catch(() => {});
  }, []);
  const folderNames = [...new Set(Object.values(pinMeta.folders))].sort();
  const shownPins = activeFolder ? myPins.filter((p) => pinMeta.folders[p.slug] === activeFolder) : myPins;
  const assignFolder = async (slug, folder) => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins/folder`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, folder }),
    });
    const j = await r.json();
    if (j.ok) setPinMeta((m) => ({ ...m, folders: j.folders }));
  };
  const shareFolder = async (folder) => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/magazine/pins/share-folder`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder }),
    });
    const j = await r.json();
    if (j.ok) {
      const link = `${window.location.origin}${j.url}`;
      try { await navigator.clipboard.writeText(link); } catch { /* noop */ }
      toast.success(`Enlace de la carpeta "${folder}" copiado — compártelo con quien quieras 🗂`);
    }
  };
  const featured = articles.filter((a) => a.featured);
  // The front page breathes: the hero rotates among the newest pieces on every visit.
  const [rot] = useState(() => Math.floor(Math.random() * 4));
  const hero = isSearching ? null : (articles[rot % Math.max(1, Math.min(4, articles.length))] || articles[0]);
  const picks = isSearching ? [] : featured.filter((f) => f.slug !== hero?.slug).slice(0, 2);
  const latest = isSearching
    ? articles
    : articles.filter((a) => a.slug !== hero?.slug && !picks.some((p) => p.slug === a.slug));

  return (
    <main className="bg-[#FAFAF7] min-h-screen pt-32 pb-24" data-testid="magazine-page">
      <SEO
        path="/magazine"
        title="Kitxens Magazine — Restaurant Operating Intelligence"
        description="The Kitxens Magazine: editorial perspectives on Restaurant AI, technology, operations and revenue growth from the team building the operating system for modern restaurants."
      />

      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Masthead */}
        <header className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <p className="text-[#F77F11] text-[11px] tracking-[0.24em] font-bold uppercase">
            Kitxens Magazine
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-neutral-900 tracking-[-0.022em] leading-[1.05]">
            Restaurant Operating <span className="text-[#F77F11]">Intelligence.</span>
          </h1>
          <p className="mt-5 text-[16px] sm:text-[17px] text-neutral-600 leading-relaxed">
            Editorial perspectives on Restaurant AI, technology, operations and growth — written by
            the team building the operating system for modern restaurants.
          </p>

          {/* Search */}
          <form
            onSubmit={onSearch}
            className="mt-8 flex items-center max-w-md mx-auto bg-white border border-neutral-200 rounded-full pl-5 pr-2 h-12 shadow-sm focus-within:border-[#F77F11] focus-within:shadow-[0_8px_24px_-12px_rgba(247,127,17,0.35)] transition-all"
            data-testid="magazine-search-form"
          >
            <Search className="w-4 h-4 text-neutral-400 shrink-0" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles…"
              className="flex-1 bg-transparent outline-none text-[14px] text-neutral-900 placeholder:text-neutral-400 px-3"
              data-testid="magazine-search-input"
              aria-label="Search articles"
            />
            <button
              type="submit"
              className="h-9 px-4 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white text-[12.5px] font-semibold transition-colors"
              data-testid="magazine-search-submit"
            >
              Search
            </button>
          </form>
        </header>

        {/* Category rail */}
        {categories.length > 0 && (
          <nav
            className="flex items-center gap-2.5 overflow-x-auto pb-3 mb-10 -mx-2 px-2 scrollbar-hide select-none"
            aria-label="Magazine categories"
            data-testid="magazine-category-rail"
          >
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/magazine/category/${c.slug}`}
                draggable="false"
                data-testid={`magazine-category-pill-${c.slug}`}
                className="shrink-0 group inline-flex items-center gap-2.5 pl-4 pr-1.5 h-10 rounded-full border border-neutral-200 bg-white text-[13px] font-semibold text-neutral-800 hover:bg-neutral-950 hover:border-neutral-950 hover:text-white active:scale-[0.97] transition-[background-color,border-color,color,transform] duration-200"
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: c.accent_color || '#F77F11' }}
                />
                {c.name}
                <span className="inline-flex items-center justify-center min-w-[26px] h-[26px] px-1.5 rounded-full bg-neutral-100 text-neutral-500 text-[11px] font-bold tabular-nums group-hover:bg-white/15 group-hover:text-white transition-colors duration-200">
                  {c.article_count}
                </span>
              </Link>
            ))}
          </nav>
        )}

        {loading && (
          <p className="text-center text-neutral-500 py-20">Loading…</p>
        )}

        {!loading && !articles.length && (
          <p className="text-center text-neutral-500 py-20" data-testid="magazine-empty">
            No articles match your search.
          </p>
        )}

        {/* Hero feature */}
        {!loading && hero && !isSearching && (
          <div className="mb-12 lg:mb-14">
            <ArticleCard article={hero} variant="feature" />
          </div>
        )}

        {/* Reader library — the articles this signed-in reader pinned */}
        {!isSearching && myPins.length > 0 && (
          <section className="mb-12" data-testid="magazine-library">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <h2 className="text-[18px] font-bold tracking-[-0.005em] text-neutral-900">📌 Tu biblioteca</h2>
              {pinMeta.streak > 0 && (
                <span data-testid="reading-streak-badge" className="inline-flex items-center gap-1 rounded-full bg-neutral-900 text-white text-[11px] font-bold px-2.5 py-1">
                  🔥 {pinMeta.streak} {pinMeta.streak === 1 ? 'semana' : 'semanas'} leyendo
                </span>
              )}
              {pinMeta.badges.map((b) => (
                <span key={b.id} data-testid={`reader-badge-${b.id}`} title={b.label}
                  className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white text-neutral-700 text-[11px] font-bold px-2.5 py-1">
                  {b.icon} {b.label}
                </span>
              ))}
            </div>
            {folderNames.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="library-folder-chips">
                <button type="button" onClick={() => setActiveFolder('')} data-testid="library-folder-all"
                  className={`px-3 py-1 rounded-full text-[12px] font-bold border transition-colors ${!activeFolder ? 'bg-[#F77F11] text-white border-[#F77F11]' : 'bg-white text-neutral-600 border-neutral-200 hover:border-[#F77F11]'}`}>
                  Todos
                </button>
                {folderNames.map((f) => (
                  <button key={f} type="button" onClick={() => setActiveFolder(f)} data-testid={`library-folder-${f}`}
                    className={`px-3 py-1 rounded-full text-[12px] font-bold border transition-colors ${activeFolder === f ? 'bg-[#F77F11] text-white border-[#F77F11]' : 'bg-white text-neutral-600 border-neutral-200 hover:border-[#F77F11]'}`}>
                    🗂 {f}
                  </button>
                ))}
                {activeFolder && (
                  <button type="button" onClick={() => shareFolder(activeFolder)} data-testid="library-share-folder-btn"
                    className="px-3 py-1 rounded-full text-[12px] font-bold border border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-700 transition-colors">
                    🔗 Compartir carpeta
                  </button>
                )}
                {activeFolder && (pinMeta.folder_views[activeFolder] || 0) > 0 && (
                  <span data-testid="library-folder-views" title="Personas que abrieron tu colección compartida"
                    className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-600 text-[12px] font-bold px-2.5 py-1">
                    👀 {pinMeta.folder_views[activeFolder]} {pinMeta.folder_views[activeFolder] === 1 ? 'apertura' : 'aperturas'}
                  </span>
                )}
              </div>
            )}
            <div className="flex gap-4 overflow-x-auto pb-2">
              {shownPins.map((p) => (
                <div key={p.slug} className="shrink-0 w-56 rounded-2xl border border-neutral-200 bg-white hover:border-[#F77F11] transition-colors overflow-hidden">
                  <Link to={`/magazine/${p.slug}`} data-testid={`library-pin-${p.slug}`}>
                    {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-24 object-cover" />}
                    <div className="p-3 pb-1">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-[#F77F11]">{p.category}</p>
                      <p className="mt-1 text-[13px] font-bold text-neutral-900 leading-snug line-clamp-2">{p.title}</p>
                    </div>
                  </Link>
                  <div className="px-3 pb-2.5">
                    <select value={pinMeta.folders[p.slug] || ''} data-testid={`library-folder-select-${p.slug}`}
                      onChange={(e) => {
                        if (e.target.value === '__new__') {
                          const name = window.prompt('Nombre de la carpeta (ej. Marketing, Costes):');
                          if (name && name.trim()) assignFolder(p.slug, name.trim());
                        } else assignFolder(p.slug, e.target.value);
                      }}
                      className="w-full text-[11px] text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg px-2 py-1 cursor-pointer">
                      <option value="">Sin carpeta</option>
                      {folderNames.map((f) => <option key={f} value={f}>{f}</option>)}
                      <option value="__new__">+ Nueva carpeta…</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
            {suggestions.length > 0 && (
              <div className="mt-6 space-y-5" data-testid="library-suggestions">
                {suggestions.map((s) => (
                  <div key={s.folder}>
                    <p className="text-[13px] font-bold text-neutral-700 mb-2.5">✨ Más sobre <span className="text-[#F77F11]">{s.folder}</span></p>
                    <div className="flex gap-4 overflow-x-auto pb-1">
                      {s.articles.map((a) => (
                        <Link key={a.slug} to={`/magazine/${a.slug}?utm_source=folder_suggestion`} data-testid={`suggestion-${a.slug}`}
                          className="shrink-0 w-56 rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/60 hover:border-[#F77F11] hover:bg-white transition-colors overflow-hidden">
                          {a.cover_image && <img src={a.cover_image} alt="" className="w-full h-20 object-cover" />}
                          <div className="p-3">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-[#F77F11]">{a.category}</p>
                            <p className="mt-1 text-[12.5px] font-bold text-neutral-800 leading-snug line-clamp-2">{a.title}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Most-saved — social proof from readers' libraries */}
        {!isSearching && popular.length > 0 && (
          <section className="mb-12" data-testid="magazine-popular">
            <div className="flex items-baseline gap-3 mb-4">
              <h2 className="text-[18px] font-bold tracking-[-0.005em] text-neutral-900">🔖 Los más guardados</h2>
              <span className="text-[12px] text-neutral-400">lo que los lectores guardan en su biblioteca</span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {popular.map((p) => (
                <Link key={p.slug} to={`/magazine/${p.slug}`} data-testid={`popular-pin-${p.slug}`}
                  className="relative shrink-0 w-56 rounded-2xl border border-neutral-200 bg-white hover:border-[#F77F11] transition-colors overflow-hidden">
                  <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 rounded-full bg-[#F77F11] text-white text-[10.5px] font-bold px-2 py-0.5">
                    📌 {p.pin_count}
                  </span>
                  {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-24 object-cover" />}
                  <div className="p-3">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#F77F11]">{p.category}</p>
                    <p className="mt-1 text-[13px] font-bold text-neutral-900 leading-snug line-clamp-2">{p.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Editor picks (2 secondary featured) */}
        {!loading && !isSearching && picks.length > 0 && (
          <section className="mb-12 lg:mb-16">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="text-[18px] font-bold tracking-[-0.005em] text-neutral-900">
                Editor’s picks
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-7">
              {picks.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </section>
        )}

        {/* Latest grid */}
        {!loading && latest.length > 0 && (
          <section>
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="text-[18px] font-bold tracking-[-0.005em] text-neutral-900">
                {isSearching ? `Results for "${queryFromUrl}"` : 'Latest articles'}
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {latest.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </section>
        )}

        {!isSearching && <MagazineExtras />}
      </div>
    </main>
  );
}
